import { useWallet } from '@txnlab/use-wallet-react';
import { useSnackbar } from 'notistack';
import { useState, useEffect, useRef, useMemo } from 'react';
import algosdk from 'algosdk';
import { getAlgodConfigFromViteEnvironment, getIndexerConfigFromViteEnvironment } from '../utils/network/getAlgoClientConfigs';
import AlgoMark from './AlgoMark';
import SpinModal from './SpinModal';

type BoxInfo = {
  addr: string;
  locked: boolean;
  lastClaimRound: number;
  lastClaimTimestamp?: number | null;
  totalClaims: number;
};


const CONTRACT_ID = 3461957850;
const CONTRACT_ADDRESS = algosdk.getApplicationAddress(CONTRACT_ID);
const CREATOR_ADDRESS = 'GET2TVGHL7H3UFFIY4VGIKYYXIVBDX6VCRZBEEW7GEHLHEGPYVLYKJRAVM';
const DELETE_LSIG_ADDRESS = 'UROWNIR6GS4KFOEG2T4DY6BVQG5YNDZJFUBMHNVP2QJPB54A4ZJIRYY3DI';

const CLAIM_SELECTOR = new TextEncoder().encode("claim");
const DELETE_SELECTOR = new TextEncoder().encode("delete");
const LOCK_SELECTOR = new TextEncoder().encode("locked");
const UNLOCK_SELECTOR = new TextEncoder().encode("unlocked");
const AMOUNT_SELECTOR = new TextEncoder().encode("amount");
const INTERV_SELECTOR = new TextEncoder().encode("interv");
const NOTE_BYTES = new TextEncoder().encode('AlgoFaucet');
const CONFIG_FEE = 8_000;

const AMOUNT_OPTS = [1_000, 2_000, 4_000, 8_000] as const;
const INTERVAL_OPTS = [5_000, 10_000, 20_000, 40_000] as const;

const MAINNET_INDEXER_URL = 'https://mainnet-idx.algonode.cloud';

function decodeBase64ToUtf8(b64: string): string {
  try {
    if (typeof Buffer !== 'undefined') return Buffer.from(b64, 'base64').toString('utf8');
    return decodeURIComponent(escape(atob(b64)));
  } catch {
    return '';
  }
}

type ConfigChangeView = {
  label: string;
  updater: string;
  txId: string;
};

type LatestConfigChanges = {
  amountChange: ConfigChangeView | null;
  intervalChange: ConfigChangeView | null;
};

function decodeUint64ArgFromB64(argB64: string): number | null {
  try {
    const bytes = Buffer.from(argB64, 'base64');
    if (bytes.length < 8) return null;
    return Number(bytes.readBigUInt64BE(0));
  } catch {
    return null;
  }
}

function formatAmountChange(from: number | null, to: number): string {
  const fromLabel = from !== null ? (from / 1_000_000).toFixed(3) : 'N/A';
  const toLabel = (to / 1_000_000).toFixed(3);
  return `${fromLabel} -> ${toLabel} ALGO`;
}

function formatIntervalChange(from: number | null, to: number): string {
  const fromLabel = from !== null ? from.toLocaleString('fr-FR') : 'N/A';
  const toLabel = to.toLocaleString('fr-FR');
  return `${fromLabel} -> ${toLabel} blocks`;
}

async function fetchLatestConfigChangesFromBlockchain(appId: number): Promise<LatestConfigChanges> {
  const amountB64 = typeof Buffer !== 'undefined' ? Buffer.from(AMOUNT_SELECTOR).toString('base64') : btoa('amount');
  const intervB64 = typeof Buffer !== 'undefined' ? Buffer.from(INTERV_SELECTOR).toString('base64') : btoa('interv');
  const baseUrl = `${MAINNET_INDEXER_URL}/v2/transactions?application-id=${appId}&tx-type=appl&limit=1000`;
  try {
    const getAppArgs = (t: any): string[] => {
      const at = t?.['application-transaction'] ?? t?.applicationTransaction;
      const args = at?.['application-args'] ?? at?.applicationArgs ?? [];
      return Array.isArray(args) ? args.map((a: any) => (typeof a === 'string' ? a : a?.bytes ?? '')).filter(Boolean) : [];
    };
    const getConfigKey = (tx: any): 'amount' | 'interv' | null => {
      const args = getAppArgs(tx);
      const first = args[0];
      if (!first) return null;
      if (first === amountB64 || decodeBase64ToUtf8(first) === 'amount') return 'amount';
      if (first === intervB64 || decodeBase64ToUtf8(first) === 'interv') return 'interv';
      return null;
    };
    const getRound = (tx: any): number => Number(tx?.['confirmed-round'] ?? tx?.confirmedRound ?? 0);
    const getIntra = (tx: any): number => Number(tx?.['intra-round-offset'] ?? tx?.intraRoundOffset ?? 0);
    const compareTxOrderDesc = (a: any, b: any): number => {
      const ra = getRound(a);
      const rb = getRound(b);
      if (ra !== rb) return rb - ra;
      return getIntra(b) - getIntra(a);
    };

    let nextToken: string | null = null;
    const allConfigTxs: any[] = [];

    for (let page = 0; page < 20; page++) {
      const requestUrl: string = nextToken ? `${baseUrl}&next=${encodeURIComponent(nextToken)}` : baseUrl;
      const response: Response = await fetch(requestUrl);
      if (!response.ok) break;
      const payload: any = await response.json();
      const txs = payload?.transactions ?? [];
      for (const tx of txs) {
        if (getConfigKey(tx)) allConfigTxs.push(tx);
      }
      nextToken = payload?.['next-token'] ?? payload?.nextToken ?? null;
      if (!nextToken || txs.length === 0) break;
    }

    const lastChangeFor = (key: 'amount' | 'interv'): ConfigChangeView | null => {
      const txs = allConfigTxs.filter((tx) => getConfigKey(tx) === key).sort(compareTxOrderDesc);
      const latestTx = txs[0];
      if (!latestTx) return null;
      const previousTx = txs[1];
      const latest = decodeUint64ArgFromB64(getAppArgs(latestTx)[1] ?? '');
      if (latest === null) return null;
      const previous = previousTx ? decodeUint64ArgFromB64(getAppArgs(previousTx)[1] ?? '') : null;
      const txId = String(latestTx.id ?? latestTx.txid ?? '');
      const updater = String(latestTx.sender ?? latestTx['sender'] ?? '');
      if (!txId || !updater) return null;
      return {
        label: key === 'amount' ? formatAmountChange(previous, latest) : formatIntervalChange(previous, latest),
        updater,
        txId,
      };
    };

    return {
      amountChange: lastChangeFor('amount'),
      intervalChange: lastChangeFor('interv'),
    };
  } catch {
    return { amountChange: null, intervalChange: null };
  }
}

interface FaucetProps {
  remainingBlocksText: string;
}

function PixelLoader() {
  return (
    <div className="px-loader px-body-center" aria-hidden="true">
      <span />
      <span />
      <span />
    </div>
  );
}

const Faucet = ({ remainingBlocksText }: FaucetProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [manageOpen, setManageOpen] = useState<boolean>(false);
  const [spinOpen, setSpinOpen] = useState<boolean>(false);
  const [manageLoading, setManageLoading] = useState<boolean>(false);
  const [manageBoxes, setManageBoxes] = useState<BoxInfo[]>([]);
  const [manageLoadingBoxes, setManageLoadingBoxes] = useState<boolean>(false);
  const [manageTab, setManageTab] = useState<'users' | 'faucet'>('users');
  const [manageUserSearch, setManageUserSearch] = useState<string>('');
  const [faucetConfig, setFaucetConfig] = useState<{ claimAmount: number; blockInterval: number }>({ claimAmount: 0, blockInterval: 0 });
  const [faucetConfigLoading, setFaucetConfigLoading] = useState(false);
  const [faucetConfigSaving, setFaucetConfigSaving] = useState(false);
  const [latestAmountChange, setLatestAmountChange] = useState<ConfigChangeView | null>(null);
  const [latestIntervalChange, setLatestIntervalChange] = useState<ConfigChangeView | null>(null);
  const manageContentRef = useRef<HTMLDivElement | null>(null);
  const faucetHeroRef = useRef<HTMLDivElement | null>(null);
  const [contractBalance, setContractBalance] = useState<number>(0);
  const [contractMBR, setContractMBR] = useState<number>(0);
  const [lsigBalance, setLsigBalance] = useState<number | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { activeAddress, transactionSigner } = useWallet();

  const algodConfig = getAlgodConfigFromViteEnvironment();
  const algodClient = new algosdk.Algodv2(
    String(algodConfig.token),
    algodConfig.server,
    algodConfig.port
  );

  const indexerConfig = useMemo(() => {
    try {
      return getIndexerConfigFromViteEnvironment();
    } catch {
      return null;
    }
  }, []);
  const indexerClient = useMemo(() => {
    if (!indexerConfig) return null;
    try {
      return new algosdk.Indexer(String(indexerConfig.token), indexerConfig.server, indexerConfig.port);
    } catch {
      return null;
    }
  }, [indexerConfig]);

  const fetchContractBalance = async (retries = 3) => {
    for (let i = 0; i < retries; i++) {
      let timeoutId: NodeJS.Timeout | null = null;
      try {
        
        const controller = new AbortController();
        timeoutId = setTimeout(() => controller.abort(), 8000);

        const accountInfo = await algodClient.accountInformation(CONTRACT_ADDRESS).do();
        if (timeoutId) clearTimeout(timeoutId);

        setContractBalance(Number(accountInfo.amount) / 1000000);
        setContractMBR(Number(accountInfo.minBalance) / 1000000);
        return;
      } catch (e: any) {
        if (timeoutId) clearTimeout(timeoutId);

        if (i === retries - 1) {
          
          if (e.name === 'AbortError') {
            enqueueSnackbar('Request timeout - please check your connection', { variant: 'warning' });
          } else {
            enqueueSnackbar('Failed to fetch contract balance', { variant: 'error' });
          }
          setContractBalance(0);
          setContractMBR(0);
        } else {
          
          const delay = 1000 * Math.pow(2, i);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
  };

  const fetchLogicSigBalance = async (retries = 2) => {
    for (let i = 0; i < retries; i++) {
      let timeoutId: NodeJS.Timeout | null = null;
      try {
        const controller = new AbortController();
        timeoutId = setTimeout(() => controller.abort(), 8000);

        const { lsig } = await getOrCompileFeePayer();
        const feeAddr = (lsig as any).address().toString();
        const info = await algodClient.accountInformation(feeAddr).do();
        if (timeoutId) clearTimeout(timeoutId);
        setLsigBalance(Number(info.amount) / 1_000_000);
        return;
      } catch (e: any) {
        if (timeoutId) clearTimeout(timeoutId);
        if (i === retries - 1) {
          setLsigBalance(null);
        } else {
          const delay = 800 * Math.pow(2, i);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
  };

  const shortenAddr = (a: string) => (a && a.length > 12 ? `${a.slice(0, 4)}...${a.slice(-4)}` : a);
  const normalizedUserSearch = manageUserSearch.trim().toLowerCase();
  const filteredManageBoxes = useMemo(
    () => (
      normalizedUserSearch
        ? manageBoxes.filter((box) => box.addr.toLowerCase().includes(normalizedUserSearch))
        : manageBoxes
    ),
    [manageBoxes, normalizedUserSearch]
  );

  useEffect(() => {
    const el = faucetHeroRef.current;
    if (!el) return;
    const sync = () => {
      document.documentElement.style.setProperty('--faucet-hero-width', `${Math.round(el.getBoundingClientRect().width)}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--faucet-hero-width');
    };
  }, [contractBalance]);

  const decodeBoxState = (boxValue: any): { locked: boolean; lastClaimRound: number } => {
    try {
      const bytes = new Uint8Array(boxValue);
      if (bytes.length < 8) return { locked: false, lastClaimRound: 0 };
      const buf = Buffer.from(bytes);
      if (bytes.length >= 9) {
        return {
          locked: bytes[0] === 1,
          lastClaimRound: Number(buf.readBigUInt64BE(1)),
        };
      }
      return {
        locked: false,
        lastClaimRound: Number(buf.readBigUInt64BE(0)),
      };
    } catch {
      return { locked: false, lastClaimRound: 0 };
    }
  };

  const formatLastClaimTime = (timestampSec?: number | null): string => {
    if (!timestampSec) return 'Unknown';
    const date = new Date(timestampSec * 1000);
    const now = Date.now();
    const diffMs = now - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    let relative: string;
    if (diffMinutes < 1) relative = 'just now';
    else if (diffMinutes < 60) relative = `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    else {
      const diffHours = Math.floor(diffMinutes / 60);
      if (diffHours < 24) relative = `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
      else {
        const diffDays = Math.floor(diffHours / 24);
        relative = `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
      }
    }
    return relative;
  };

  const pLimit = (n: number) => {
    let active = 0;
    const queue: Array<() => void> = [];
    const next = () => {
      active--;
      const fn = queue.shift();
      if (fn) fn();
    };
    return async <T,>(fn: () => Promise<T>): Promise<T> =>
      await new Promise<T>((resolve, reject) => {
        const run = () => {
          active++;
          fn().then(resolve, reject).finally(next);
        };
        if (active < n) run();
        else queue.push(run);
      });
  };

  const fetchClaimStats = async (_contractAddr: string, userAddr: string): Promise<{ totalClaims: number; lastClaimTimestamp?: number | null }> => {
    if (!indexerClient) return { totalClaims: 1 };
    try {
      const appRes = await (indexerClient as any).searchForTransactions().applicationID(CONTRACT_ID).txType('appl').address(userAddr).addressRole('sender').limit(1000).do();
      const appTxs = (appRes?.transactions || []) as any[];
      const claimTxs = appTxs.filter((tx: any) => {
        const appArgs = tx?.applicationTransaction?.applicationArgs || [];
        if (appArgs.length === 0) return false;
        try {
          return Buffer.from(appArgs[0], 'base64').toString() === 'claim';
        } catch { return false; }
      });
      let totalClaims = claimTxs.length;
      let lastClaimTimestamp: number | null = null;
      if (claimTxs.length > 0) {
        try {
          const latestTx = claimTxs.reduce((acc: any, tx: any) => {
            const accRt = acc['round-time'] ?? acc.roundTime ?? 0;
            const txRt = tx['round-time'] ?? tx.roundTime ?? 0;
            return txRt > accRt ? tx : acc;
          }, claimTxs[0]);
          const rt = latestTx['round-time'] ?? latestTx.roundTime;
          if (typeof rt === 'number') lastClaimTimestamp = rt;
        } catch {}
      }
      if (totalClaims === 0) totalClaims = 1;
      return { totalClaims, lastClaimTimestamp };
    } catch {
      return { totalClaims: 0 };
    }
  };

  const refreshManageBoxes = async () => {
    const known = new Map(manageBoxes.map((box) => [box.addr, box]));
    if (known.size === 0) setManageLoadingBoxes(true);
    try {
      const res = await algodClient.getApplicationBoxes(CONTRACT_ID).do();
      const names: Uint8Array[] = [];
      for (const b of res.boxes || []) {
        const nameB64 = (b as any)?.name;
        if (!nameB64) continue;
        try {
          const bytes = Uint8Array.from(Buffer.from(nameB64, 'base64'));
          names.push(bytes);
        } catch {}
      }
      const sortBoxes = (rows: BoxInfo[]) => rows.sort((a, b) => (b.lastClaimRound - a.lastClaimRound) || a.addr.localeCompare(b.addr));
      const limit = pLimit(10);
      const infos = await Promise.all(
        names.map((name) =>
          limit(async () => {
            const addr = algosdk.encodeAddress(name);
            const previous = known.get(addr);
            let decoded = { locked: previous?.locked ?? false, lastClaimRound: previous?.lastClaimRound ?? 0 };
            try {
              const box = await algodClient.getApplicationBoxByName(CONTRACT_ID, name).do();
              if (box?.value) decoded = decodeBoxState(box.value);
            } catch {}
            const stats = await fetchClaimStats(CONTRACT_ADDRESS, addr);
            const lastClaimTimestamp = typeof stats.lastClaimTimestamp === 'number' ? stats.lastClaimTimestamp : previous?.lastClaimTimestamp ?? null;
            return {
              addr,
              locked: decoded.locked,
              lastClaimRound: decoded.lastClaimRound,
              lastClaimTimestamp,
              totalClaims: stats.totalClaims || previous?.totalClaims || 0,
            };
          }),
        ),
      );
      setManageBoxes(sortBoxes(infos));
    } catch (e) {
      enqueueSnackbar('Failed to load boxes', { variant: 'error' });
      if (known.size === 0) setManageBoxes([]);
    }
    setManageLoadingBoxes(false);
  };

  const refreshFaucetConfig = async () => {
    setFaucetConfigLoading(true);
    try {
      const app = await algodClient.getApplicationByID(CONTRACT_ID).do();
      const params = (app as any).params || {};
      const globalState = params['global-state'] ?? params.globalState ?? [];
      let claimAmount = 2_000;
      let blockInterval = 10_000;
      for (const g of globalState) {
        let key = '';
        try {
          const raw = g.key;
          if (typeof raw === 'string') {
            key = Buffer.from(raw, 'base64').toString('utf8').replace(/\0/g, '');
          } else if (raw && typeof raw === 'object' && raw.constructor?.name === 'Uint8Array') {
            key = new TextDecoder().decode(raw).replace(/\0/g, '');
          }
        } catch {
          // skip entry
        }
        const val = g.value;
        const type = val?.type ?? val?.value?.type;
        const uintRaw = val?.uint ?? val?.value?.uint;
        if (type === 2 && uintRaw !== undefined && uintRaw !== null) {
          const num = Number(uintRaw);
          if (!Number.isNaN(num)) {
            if (key === 'claim_amount') claimAmount = num;
            if (key === 'block_interval') blockInterval = num;
          }
        }
      }
      setFaucetConfig({ claimAmount, blockInterval });

      const changes = await fetchLatestConfigChangesFromBlockchain(CONTRACT_ID);
      setLatestAmountChange(changes.amountChange);
      setLatestIntervalChange(changes.intervalChange);
    } catch (e) {
      console.warn('[Faucet] refreshFaucetConfig failed:', e);
      setFaucetConfig({ claimAmount: 2_000, blockInterval: 10_000 });
      
    }
    setFaucetConfigLoading(false);
  };

  const switchManageTab = (tab: 'users' | 'faucet') => {
    if (tab === manageTab) return;
    setManageTab(tab);
    if (manageContentRef.current) manageContentRef.current.scrollTop = 0;
    if (tab === 'faucet') refreshFaucetConfig();
  };

  const encodeUint64 = (n: number) => {
    const buf = new Uint8Array(8);
    new DataView(buf.buffer).setBigUint64(0, BigInt(n), false);
    return buf;
  };

  const performSetAmount = async (amountMicro: number) => {
    if (!activeAddress || !transactionSigner) {
      enqueueSnackbar('Please connect your wallet', { variant: 'error' });
      return;
    }
    if (!AMOUNT_OPTS.includes(amountMicro as any)) return;
    setFaucetConfigSaving(true);
    try {
      const sp = await algodClient.getTransactionParams().do();
      const paymentTxn = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
        sender: activeAddress,
        receiver: CONTRACT_ADDRESS,
        amount: CONFIG_FEE,
        suggestedParams: { ...sp, flatFee: true, fee: 1000 },
      });
      const appTxn = algosdk.makeApplicationCallTxnFromObject({
        sender: activeAddress,
        appIndex: CONTRACT_ID,
        onComplete: algosdk.OnApplicationComplete.NoOpOC,
        appArgs: [AMOUNT_SELECTOR, encodeUint64(amountMicro)],
        suggestedParams: { ...sp, flatFee: true, fee: 1000 },
      });
      const [g0, g1] = algosdk.assignGroupID([paymentTxn as any, appTxn as any]);
      const configUpdateTxId = (g1 as any).txID().toString();
      const signed = await transactionSigner([g0 as any, g1 as any], [0, 1]);
      const res = await algodClient.sendRawTransaction(signed).do();
      await algosdk.waitForConfirmation(algodClient, res.txid, 10);
      enqueueSnackbar('Claim amount updated', { variant: 'success' });
      setFaucetConfig((c) => ({ ...c, claimAmount: amountMicro }));
      setLatestAmountChange({
        label: formatAmountChange(faucetConfig.claimAmount, amountMicro),
        updater: activeAddress,
        txId: configUpdateTxId,
      });
    } catch (e: any) {
      enqueueSnackbar(`Set amount failed: ${e?.message || 'error'}`, { variant: 'error' });
    }
    setFaucetConfigSaving(false);
  };

  const performSetInterval = async (intervalBlocks: number) => {
    if (!activeAddress || !transactionSigner) {
      enqueueSnackbar('Please connect your wallet', { variant: 'error' });
      return;
    }
    if (!INTERVAL_OPTS.includes(intervalBlocks as any)) return;
    setFaucetConfigSaving(true);
    try {
      const sp = await algodClient.getTransactionParams().do();
      const paymentTxn = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
        sender: activeAddress,
        receiver: CREATOR_ADDRESS,
        amount: CONFIG_FEE,
        suggestedParams: { ...sp, flatFee: true, fee: 1000 },
      });
      const appTxn = algosdk.makeApplicationCallTxnFromObject({
        sender: activeAddress,
        appIndex: CONTRACT_ID,
        onComplete: algosdk.OnApplicationComplete.NoOpOC,
        appArgs: [INTERV_SELECTOR, encodeUint64(intervalBlocks)],
        suggestedParams: { ...sp, flatFee: true, fee: 1000 },
      });
      const [g0, g1] = algosdk.assignGroupID([paymentTxn as any, appTxn as any]);
      const configUpdateTxId = (g1 as any).txID().toString();
      const signed = await transactionSigner([g0 as any, g1 as any], [0, 1]);
      const res = await algodClient.sendRawTransaction(signed).do();
      await algosdk.waitForConfirmation(algodClient, res.txid, 10);
      enqueueSnackbar('Block interval updated', { variant: 'success' });
      setFaucetConfig((c) => ({ ...c, blockInterval: intervalBlocks }));
      setLatestIntervalChange({
        label: formatIntervalChange(faucetConfig.blockInterval, intervalBlocks),
        updater: activeAddress,
        txId: configUpdateTxId,
      });
    } catch (e: any) {
      enqueueSnackbar(`Set interval failed: ${e?.message || 'error'}`, { variant: 'error' });
    }
    setFaucetConfigSaving(false);
  };

  useEffect(() => {
    if (manageOpen) {
      refreshManageBoxes();
      refreshFaucetConfig();
    }
  }, [manageOpen, manageTab]);

  useEffect(() => {
    fetchContractBalance();
    fetchLogicSigBalance();
  }, []);


  const verifyNetwork = async (): Promise<boolean> => {
    try {
      const params = await algodClient.getTransactionParams().do();
      return params.genesisID.toLowerCase().startsWith('mainnet');
    } catch (e) {
      return false;
    }
  };

  
  const feePayerLsigRef = useRef<any>(null);
  const feePayerAddrRef = useRef<string | null>(null);

  const getOrCompileFeePayer = async (): Promise<{ lsig: any; address: string }> => {
    if (feePayerLsigRef.current && feePayerAddrRef.current) {
      return { lsig: feePayerLsigRef.current, address: feePayerAddrRef.current };
    }
    const teal = `#pragma version 6
txn TypeEnum
int pay
==

txn Receiver
txn Sender
==
&&

txn Amount
int 0
==
&&

txn RekeyTo
global ZeroAddress
==
&&

txn CloseRemainderTo
global ZeroAddress
==
&&

txn Fee
int 3000
>=
&&

txn GroupIndex
int 1
-
store 0

load 0
gtxns TypeEnum
int appl
==
&&

load 0
gtxns ApplicationID
int ${CONTRACT_ID}
==
&&

load 0
gtxns Fee
int 0
==
&&

load 0
gtxns NumAppArgs
int 1
>=
&&

load 0
gtxnsa ApplicationArgs 0
byte "claim"
==
&&

return`;
    const compileRes = await algodClient.compile(new TextEncoder().encode(teal)).do();
    const program = Uint8Array.from(Buffer.from(compileRes.result, 'base64'));
    const lsig = new (algosdk as any).LogicSigAccount(program);
    let addr: string | null = null;
    try {
      
      addr = lsig.address().toString();
    } catch (e) {
      console.warn('[Faucet] Could not derive address from program, fallback to compile hash', e);
    }
    if (!addr && compileRes && typeof compileRes.hash === 'string') {
      addr = compileRes.hash;
    }
    
    if (!addr || !algosdk.isValidAddress(addr)) {
      console.error('[Faucet] Invalid fee-payer address:', addr, 'compileRes.hash:', compileRes?.hash);
      throw new Error('Unable to determine fee-payer address');
    }
    if (!addr) {
      throw new Error('Unable to determine fee-payer address');
    }
    feePayerLsigRef.current = lsig;
    feePayerAddrRef.current = addr;
    return { lsig, address: addr };
  };
  const _keepRef = getOrCompileFeePayer; if (false) { console.debug(_keepRef); }

  const handleClaim = async () => {
    if (!activeAddress || !transactionSigner) {
      enqueueSnackbar('Please connect your wallet', { variant: 'error' });
      return;
    }

    const isMainnet = await verifyNetwork();
    if (!isMainnet) {
      enqueueSnackbar('Please switch to MainNet in your wallet', { variant: 'error' });
      return;
    }

    if (remainingBlocksText !== "Claim available!") {
      enqueueSnackbar('Claim not available yet', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const suggestedParams = await algodClient.getTransactionParams().do();

      const userBoxName = algosdk.decodeAddress(activeAddress).publicKey;

      const appCallTxn = algosdk.makeApplicationCallTxnFromObject({
        sender: activeAddress,
        appIndex: CONTRACT_ID,
        onComplete: algosdk.OnApplicationComplete.NoOpOC,
        appArgs: [CLAIM_SELECTOR],
        boxes: [{ appIndex: CONTRACT_ID, name: userBoxName }],
        note: NOTE_BYTES,
        suggestedParams: { ...suggestedParams, fee: 0, flatFee: true }
      });

      const { lsig: feePayerLsig } = await getOrCompileFeePayer();
      const feeAddr = (feePayerLsig as any).address().toString();
      if (!feeAddr || !algosdk.isValidAddress(feeAddr)) {
        throw new Error('Invalid fee-payer address');
      }
      const paramsFee = await algodClient.getTransactionParams().do();
      const feePayTxn: any = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
        sender: feeAddr,
        receiver: feeAddr,
        amount: 0,
        suggestedParams: { ...paramsFee, fee: 3000, flatFee: true } as any,
      } as any);

      const [g0, g1] = algosdk.assignGroupID([appCallTxn as any, feePayTxn as any]);
      const signedUser = await transactionSigner([g0 as any, g1 as any], [0]);
      const signedFee = (algosdk as any).signLogicSigTransaction(g1 as any, feePayerLsig as any).blob;
      const response = await algodClient.sendRawTransaction([signedUser[0] as unknown as Uint8Array, signedFee]).do();
      await algosdk.waitForConfirmation(algodClient, response.txid, 4);

      await fetchContractBalance();
      enqueueSnackbar('Successfully claimed ALGO!', { variant: 'success' });
      window.dispatchEvent(new Event('algofaucet:refresh-balance'));

    } catch (e: any) {
      console.error('Claim error:', e);
      const rawMessage = e?.message || '';
      const userMessage = rawMessage.includes('below min 100000')
        ? 'The LogicSig balance is insufficient.'
        : `Failed to claim: ${rawMessage || 'Please try again later.'}`;
      enqueueSnackbar(userMessage, { variant: 'error' });
    }
    setLoading(false);
  };

  const performDeleteBox = async (boxAddress: string) => {
    if (!activeAddress || !transactionSigner) {
      enqueueSnackbar('Please connect your wallet', { variant: 'error' });
      return;
    }
    setManageLoading(true);
    try {
      const suggestedParams = await algodClient.getTransactionParams().do();
      const boxName = algosdk.decodeAddress(boxAddress).publicKey;

      const paymentTxn = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
        sender: activeAddress,
        receiver: DELETE_LSIG_ADDRESS,
        amount: CONFIG_FEE,
        suggestedParams: { ...suggestedParams, flatFee: true, fee: 1000 },
      });
      const deleteTxn = algosdk.makeApplicationCallTxnFromObject({
        sender: activeAddress,
        appIndex: CONTRACT_ID,
        onComplete: algosdk.OnApplicationComplete.NoOpOC,
        appArgs: [DELETE_SELECTOR, boxName],
        boxes: [{ appIndex: CONTRACT_ID, name: boxName }],
        suggestedParams: { ...suggestedParams, flatFee: true, fee: 1000 },
      });

      const [payWithGroup, deleteWithGroup] = algosdk.assignGroupID([paymentTxn as any, deleteTxn as any]);
      const signed = await transactionSigner([payWithGroup as any, deleteWithGroup as any], [0, 1]);
      const res = await algodClient.sendRawTransaction(signed).do();
      await algosdk.waitForConfirmation(algodClient, res.txid, 10);
      enqueueSnackbar('Box deleted', { variant: 'success' });
      await refreshManageBoxes();
      window.dispatchEvent(new Event('algofaucet:refresh-balance'));
    } catch (e: any) {
      enqueueSnackbar(`Delete failed: ${e?.message || 'error'}`, { variant: 'error' });
    }
    setManageLoading(false);
  };

  const performToggleLock = async (boxAddress: string, locked: boolean) => {
    if (!activeAddress || !transactionSigner) {
      enqueueSnackbar('Please connect your wallet', { variant: 'error' });
      return;
    }
    setManageLoading(true);
    try {
      const suggestedParams = await algodClient.getTransactionParams().do();
      const boxName = algosdk.decodeAddress(boxAddress).publicKey;
      const actionSelector = locked ? UNLOCK_SELECTOR : LOCK_SELECTOR;
      const paymentReceiver = locked ? CREATOR_ADDRESS : CONTRACT_ADDRESS;
      const appTxn = algosdk.makeApplicationCallTxnFromObject({
        sender: activeAddress,
        appIndex: CONTRACT_ID,
        onComplete: algosdk.OnApplicationComplete.NoOpOC,
        appArgs: [actionSelector, boxName],
        boxes: [{ appIndex: CONTRACT_ID, name: boxName }],
        suggestedParams: { ...suggestedParams, flatFee: true, fee: 1000 },
      });

      let signed: Uint8Array[];
      if (activeAddress === CREATOR_ADDRESS) {
        signed = await transactionSigner([appTxn as any], [0]) as Uint8Array[];
      } else {
        const paymentTxn = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
          sender: activeAddress,
          receiver: paymentReceiver,
          amount: CONFIG_FEE,
          suggestedParams: { ...suggestedParams, flatFee: true, fee: 1000 },
        });
        const [payWithGroup, appWithGroup] = algosdk.assignGroupID([paymentTxn as any, appTxn as any]);
        signed = await transactionSigner([payWithGroup as any, appWithGroup as any], [0, 1]) as Uint8Array[];
      }

      const res = await algodClient.sendRawTransaction(signed).do();
      await algosdk.waitForConfirmation(algodClient, res.txid, 10);
      enqueueSnackbar(locked ? 'Address unlocked' : 'Address locked', { variant: 'success' });
      await refreshManageBoxes();
    } catch (e: any) {
      const actionLabel = locked ? 'Unlock' : 'Lock';
      enqueueSnackbar(`${actionLabel} failed: ${e?.message || 'error'}`, { variant: 'error' });
    }
    setManageLoading(false);
  };

  return (
    <>
        {manageOpen && (
          <div className="px-overlay" data-modal-root="true">
            <div className="px-modal px-modal-fixed">
              <div className="px-modal-head">
                <div className="px-tabs">
                  <span className="px-btn">{manageBoxes.length} BOX</span>
                  <button className={`px-btn${manageTab === 'users' ? ' is-on' : ''}`} type="button" onClick={() => switchManageTab('users')}>USERS</button>
                  <button className={`px-btn${manageTab === 'faucet' ? ' is-on' : ''}`} type="button" onClick={() => switchManageTab('faucet')}>FAUCET</button>
                </div>
                <button className="px-btn" type="button" onClick={() => setManageOpen(false)}>X</button>
              </div>
              {manageTab === 'users' && !manageLoadingBoxes && (
                <div className="px-modal-search">
                  <input
                    className="px-input"
                    type="text"
                    value={manageUserSearch}
                    onChange={(e) => setManageUserSearch(e.target.value)}
                    placeholder="Search address..."
                  />
                </div>
              )}
              <div ref={manageContentRef} className="px-body hidden-scrollbar">
                {manageTab === 'users' && (
                  manageLoadingBoxes ? (
                    <PixelLoader />
                  ) : manageBoxes.length === 0 ? (
                    <div>No box</div>
                  ) : filteredManageBoxes.length === 0 ? (
                    <div>No matching address</div>
                  ) : (
                    filteredManageBoxes.map((b) => (
                      <div key={b.addr} className={`px-row${b.locked ? ' is-locked' : ''}`}>
                        <div>
                          <a className="px-mono" href={`https://explorer.perawallet.app/address/${b.addr}/`} target="_blank" rel="noopener noreferrer">
                            {shortenAddr(b.addr)}
                          </a>
                          <div>Claims {b.totalClaims} · {formatLastClaimTime(b.lastClaimTimestamp)}</div>
                        </div>
                        <div className="px-tabs">
                          <button className="px-btn px-btn-lock" type="button" disabled={manageLoading} onClick={() => performToggleLock(b.addr, b.locked)}>
                            {b.locked ? 'UNLOCK' : 'LOCK'}
                          </button>
                          <button className="px-btn" type="button" disabled={manageLoading} onClick={() => performDeleteBox(b.addr)}>DEL</button>
                        </div>
                      </div>
                    ))
                  )
                )}
                {manageTab === 'faucet' && (
                  faucetConfigLoading ? (
                    <PixelLoader />
                  ) : (
                    <div className="px-body-center">
                      <div className="px-stat">
                        <span>Claim amount</span>
                        <span>
                          {latestAmountChange ? (
                            <>
                              <a className="px-mono" href={`https://explorer.perawallet.app/tx/${latestAmountChange.txId}/`} target="_blank" rel="noopener noreferrer">
                                {shortenAddr(latestAmountChange.updater)}
                              </a>
                              {` ${latestAmountChange.label}`}
                            </>
                          ) : (
                            `${(faucetConfig.claimAmount / 1_000_000).toFixed(3)} ALGO`
                          )}
                        </span>
                      </div>
                      <div className="px-chip-row">
                        {AMOUNT_OPTS.map((amt) => (
                          <button
                            key={amt}
                            type="button"
                            className={`px-btn${faucetConfig.claimAmount === amt ? ' is-on' : ''}`}
                            disabled={faucetConfigSaving}
                            onClick={() => performSetAmount(amt)}
                          >
                            {(amt / 1_000_000).toFixed(3)}
                          </button>
                        ))}
                      </div>
                      <div className="px-stat">
                        <span>Block interval</span>
                        <span>
                          {latestIntervalChange ? (
                            <>
                              <a className="px-mono" href={`https://explorer.perawallet.app/tx/${latestIntervalChange.txId}/`} target="_blank" rel="noopener noreferrer">
                                {shortenAddr(latestIntervalChange.updater)}
                              </a>
                              {` ${latestIntervalChange.label}`}
                            </>
                          ) : (
                            `${faucetConfig.blockInterval.toLocaleString('fr-FR')} blocks`
                          )}
                        </span>
                      </div>
                      <div className="px-chip-row">
                        {INTERVAL_OPTS.map((interval) => (
                          <button
                            key={interval}
                            type="button"
                            className={`px-btn${faucetConfig.blockInterval === interval ? ' is-on' : ''}`}
                            disabled={faucetConfigSaving}
                            onClick={() => performSetInterval(interval)}
                          >
                            {interval >= 1000 ? `${interval / 1000}k` : interval}
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        )}
        <div className="px-panel">
          <h2 className="px-title">FAUCET</h2>
          <div className="px-faucet-block">
            <div className="px-balance" ref={faucetHeroRef}>
              <span className="px-balance-line">
                <span>[</span>
                <span className="px-balance-num">{contractBalance}<AlgoMark /></span>
                <span>]</span>
              </span>
            </div>
            <div className="px-meta">
              <span className="px-meta-pair">
                <span className="px-meta-label">MBR</span>
                <span className="px-meta-amt">{contractMBR}<AlgoMark /></span>
              </span>
              <span className="px-meta-pair">
                <span className="px-meta-label">LS</span>
                {lsigBalance === null ? '—' : (
                  <span className="px-meta-amt">{lsigBalance}<AlgoMark /></span>
                )}
              </span>
            </div>
            <div className="px-status">{remainingBlocksText}</div>
            <div className="px-actions">
              <div className="px-actions-row">
                <button
                  className="px-btn"
                  onClick={handleClaim}
                  disabled={loading || remainingBlocksText !== "Claim available!"}
                  type="button"
                >
                  {loading ? 'WAIT...' : 'CLAIM'}
                </button>
                <button
                  className="px-btn"
                  type="button"
                  onClick={() => setSpinOpen(true)}
                >
                  SPIN
                </button>
              </div>
              <button
                className="px-link"
                type="button"
                disabled={!activeAddress}
                onClick={activeAddress ? () => {
                  if (manageBoxes.length === 0) setManageLoadingBoxes(true);
                  setManageOpen(true);
                } : undefined}
              >
                Manage
              </button>
            </div>
          </div>
        </div>
        <SpinModal open={spinOpen} onClose={() => setSpinOpen(false)} />
    </>
  );
};

export default Faucet;
